import { usePasswordValidation } from "@/hooks/usePasswordValidation";
import { createPasswordFieldStyles, createPasswordVisibilityToggle, getPasswordInputType } from "@/utils/authUtils";
import { SxProps, Theme } from "@mui/material";
import { useState } from "react";

export const useProfilePassword = () => {
  const { passwordErrors, validatePasswordField, clearPasswordErrors, hasPasswordErrors } = usePasswordValidation();
  
  const EMPTY_PASSWORD_PLACEHOLDER = "Set a new password";
  
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  
  // Check if user has actually started typing a new password (not placeholder values)
  const isValidPasswordInput = (newPassword: string) => {
    return !!(newPassword && 
              newPassword !== EMPTY_PASSWORD_PLACEHOLDER &&
              newPassword.trim() !== "");
  };
  
  const hasNewPasswordValue = (newPassword: string) => isValidPasswordInput(newPassword);
  const hasValidNewPassword = (newPassword: string) => isValidPasswordInput(newPassword);
  
  // Create password visibility toggle component for new password
  const createNewPasswordVisibilityToggleComponent = (newPassword: string) => {
    return createPasswordVisibilityToggle(
      showNewPassword,
      () => setShowNewPassword(!showNewPassword),
      hasNewPasswordValue(newPassword)
    );
  };

  // Create password visibility toggle component for current password
  const createCurrentPasswordVisibilityToggleComponent = (currentPassword: string) => {
    return createPasswordVisibilityToggle(
      showCurrentPassword,
      () => setShowCurrentPassword(!showCurrentPassword),
      !!currentPassword.trim()
    );
  };
  
  // Handle password field focus - clear placeholder when user starts typing
  const handlePasswordFocus = (newPassword: string, setNewPassword: (value: string) => void) => {
    if (newPassword === EMPTY_PASSWORD_PLACEHOLDER) {
      setNewPassword("");
    }
  };
  
  // Handle password field change with validation
  const handlePasswordChange = (
    value: string, 
    setNewPassword: (value: string) => void,
    setError: (error: string | null) => void
  ) => {
    setNewPassword(value);
    setError(null); // Clear any previous errors
    
    // Validate new password in real-time
    if (value && value !== EMPTY_PASSWORD_PLACEHOLDER) {
      validatePasswordField(value);
    } else {
      clearPasswordErrors();
    }
  };
  
  // Validate password for form submission
  const validatePasswordForSubmission = (newPassword: string) => {
    if (hasValidNewPassword(newPassword)) {
      const passwordValidation = validatePasswordField(newPassword);
      return passwordValidation.isValid;
    }
    return true; // No password change is valid
  };
  
  // Reset password field to placeholder after successful save
  const resetPasswordField = (setNewPassword: (value: string) => void) => {
    setNewPassword(EMPTY_PASSWORD_PLACEHOLDER);
    setShowNewPassword(false); // Hide password when resetting
  };
  
  // Get new password field props for TextField component
  const getNewPasswordFieldProps = (
    newPassword: string,
    setNewPassword: (value: string) => void,
    setError: (error: string | null) => void,
    baseStyles?: SxProps<Theme>
  ) => ({
    type: getPasswordInputType(showNewPassword), // Use browser-aware password input type
    value: newPassword,
    placeholder: EMPTY_PASSWORD_PLACEHOLDER,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => 
      handlePasswordChange(e.target.value, setNewPassword, setError),
    onFocus: () => handlePasswordFocus(newPassword, setNewPassword),
    error: hasPasswordErrors,
    helperText: hasPasswordErrors ? passwordErrors[0] : undefined,
    // CSS-based password masking for all browsers
    sx: createPasswordFieldStyles(showNewPassword, baseStyles)
  });

  // Get current password field props for TextField component
  const getCurrentPasswordFieldProps = (currentPassword: string, baseStyles?: SxProps<Theme>) => ({
    type: getPasswordInputType(showCurrentPassword), // Use browser-aware password input type
    value: currentPassword,
    placeholder: "Current Password",
    // CSS-based password masking for all browsers
    sx: createPasswordFieldStyles(showCurrentPassword, baseStyles)
  });
  
  return {
    // Constants
    EMPTY_PASSWORD_PLACEHOLDER,
    
    // State
    showNewPassword,
    showCurrentPassword,
    
    // Validation
    passwordErrors,
    hasPasswordErrors,
    clearPasswordErrors,
    
    // Helper functions
    hasNewPasswordValue,
    hasValidNewPassword,
    validatePasswordForSubmission,
    
    // Event handlers
    resetPasswordField,
    
    // UI components
    createNewPasswordVisibilityToggleComponent,
    createCurrentPasswordVisibilityToggleComponent,
    getNewPasswordFieldProps,
    getCurrentPasswordFieldProps,
  };
}; 